import banIcon from "@tabler/icons/outline/ban.svg";
import React from "react";
import { defineMessages, useIntl } from "react-intl";

import {
  useBlockAccount,
  useFollowAccount,
  useMuteAccount,
  useUnblockAccount,
  useUnfollowAccount,
  useUnmuteAccount,
} from "@/api/accounts";
import { Button } from "@/components/ui";
import { type ButtonSizes, type ButtonProps } from "@/components/ui/button/button";
import { useAuth } from "@/contexts/auth/useAuth";
import { useEngagementCapture } from "@/hooks/useEngagementCapture";

import type { Account, Status } from "@/schemas";

const messages = defineMessages({
  block: { id: "account.block", defaultMessage: "Block @{name}" },
  blocked: { id: "account.blocked", defaultMessage: "Blocked" },
  edit_profile: { id: "account.edit_profile", defaultMessage: "Edit profile" },
  follow: { id: "account.follow", defaultMessage: "Follow" },
  mute: { id: "account.mute", defaultMessage: "Mute @{name}" },
  unblock: { id: "account.unblock", defaultMessage: "Unblock @{name}" },
  unfollow: { id: "account.unfollow", defaultMessage: "Unfollow" },
  unmute: { id: "account.unmute", defaultMessage: "Unmute @{name}" },
});

interface ActionButtonProps {
  /** Target account for the action. */
  account: Account;
  /** Type of action to prioritize, eg on Blocks and Mutes pages. */
  actionType?: "muting" | "blocking";
  /** Size of the button */
  buttonSize?: ButtonSizes;
  /**
   * The post this follow button is rendered against (e.g. a detail view's author button).
   * Engagement capture needs a "post acted on" to attribute the follow to — with
   * none given (profile page, search/suggestion rows), a follow here emits no engagement event.
   */
  promptingStatus?: Status;
}

/**
 * Circumstantial action button (usually "Follow") to display on accounts.
 * May say "Unblock" or something else, depending on the relationship and
 * `actionType` prop.
 */
const ActionButton: React.FC<ActionButtonProps> = ({
  account,
  actionType,
  buttonSize = "default",
  promptingStatus,
}) => {
  const intl = useIntl();
  const { captureEngagement } = useEngagementCapture();

  const muteAccount = useMuteAccount();
  const unmuteAccount = useUnmuteAccount();
  const blockAccount = useBlockAccount();
  const unblockAccount = useUnblockAccount();
  const followAccount = useFollowAccount();
  const unfollowAccount = useUnfollowAccount();

  const { currentAccount } = useAuth();

  const handleFollow = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (account.relationship?.following) {
      unfollowAccount.mutate({ account });
    } else {
      followAccount.mutate({ account });
      if (promptingStatus) {
        captureEngagement("follow", promptingStatus, account.id);
      }
    }
  };

  const handleBlock = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (account.relationship?.blocking) {
      unblockAccount.mutate({ account });
    } else {
      blockAccount.mutate({ account });
    }
  };

  const handleMute = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (account.relationship?.muting) {
      unmuteAccount.mutate({ account });
    } else {
      muteAccount.mutate({ account });
    }
  };

  /** Handles actionType='muting' */
  const mutingAction = () => {
    const isMuted = account.relationship?.muting;
    const messageKey = isMuted ? messages.unmute : messages.mute;
    const text = intl.formatMessage(messageKey, { name: account.username });

    return (
      <Button
        variant={isMuted ? "destructive" : "outline"}
        size={buttonSize}
        text={text}
        onClick={handleMute}
      />
    );
  };

  /** Handles actionType='blocking' */
  const blockingAction = () => {
    const isBlocked = account.relationship?.blocking;
    const messageKey = isBlocked ? messages.unblock : messages.block;
    const text = intl.formatMessage(messageKey, { name: account.username });

    return (
      <Button
        variant={isBlocked ? "destructive" : "outline"}
        size={buttonSize}
        text={text}
        onClick={handleBlock}
      />
    );
  };

  if (!currentAccount) {
    return null;
  }

  if (currentAccount.id !== account.id) {
    const isFollowing = account.relationship?.following;
    const blockedBy = account.relationship?.blocked_by as boolean;

    if (actionType) {
      if (actionType === "muting") {
        return mutingAction();
      } else if (actionType === "blocking") {
        return blockingAction();
      }
    }

    if (!account.relationship) {
      // Wait until the relationship is loaded
      return null;
    } else if (account.relationship.blocking) {
      return blockingAction();
    } else if (account.relationship.muting) {
      return mutingAction();
    } else if (
      !account.relationship?.blocking &&
      !account.relationship?.muting
    ) {
      let variant: ButtonProps["variant"] = "default";
      if (blockedBy) {
        variant = "destructive";
      } else if (isFollowing) {
        variant = "secondary";
      }
      // Follow & Unfollow
      return (
        <Button
          size={buttonSize}
          disabled={
            blockedBy || followAccount.isLoading || unfollowAccount.isLoading
          }
          variant={variant}
          icon={blockedBy ? (banIcon ?? undefined) : undefined}
          onClick={handleFollow}
        >
          {isFollowing
            ? intl.formatMessage(messages.unfollow)
            : intl.formatMessage(
                blockedBy ? messages.blocked : messages.follow,
              )}
        </Button>
      );
    } else if (account.relationship?.blocking) {
      // Unblock
      return (
        <Button
          variant="destructive"
          size={buttonSize}
          text={intl.formatMessage(messages.unblock, {
            name: account.username,
          })}
          onClick={handleBlock}
        />
      );
    }
  } else {
    // Edit profile
    return (
      <Button
        variant="outline"
        size={buttonSize}
        text={intl.formatMessage(messages.edit_profile)}
        to="/settings/profile"
      />
    );
  }

  return null;
};

export default ActionButton;
